import { useQuery } from "convex/react";
import { Link, useMatchRoute } from "@tanstack/react-router";
import {
  AppWindow,
  Check,
  Copy,
  HelpCircle,
  History,
  Inbox,
  LogOut,
  MessageSquare,
  Moon,
  NotebookPen,
  Pencil,
  Puzzle,
  Settings,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { UnreadBadge } from "@/components/inbox/UnreadBadge";
import { CreditBalancePill } from "@/components/layout/CreditBalancePill";
import { SupportModal } from "@/components/support/SupportModal";
import { Toast } from "@/components/ui/Toast";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useTheme } from "@/hooks/useTheme";
import { BrandLogo, ClawMark } from "@/components/ui/BrandLogo";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/store/sidebarStore";
import { api } from "@convex/_generated/api";

interface RecentThread {
  _id: string;
  title: string;
}

interface SidebarProps {
  userId?: string;
  userName?: string;
  userImage?: string;
  recentThreads?: RecentThread[];
  onNewChat: () => void;
  onSignOut: () => void;
  onDeleteThread?: (args: { threadId: string }) => void;
  onRenameThread?: (args: { threadId: string; title: string }) => void;
}

const navItems = [
  { to: "/inbox" as const, icon: Inbox, labelKey: "nav.inbox" },
  // Tasks + Workflows hidden from the nav (operator decision, 2026-08-08) —
  // the routes stay live and URL-reachable.
  { to: "/studio" as const, icon: AppWindow, labelKey: "nav.studio" },
  { to: "/notebook" as const, icon: NotebookPen, labelKey: "nav.notebook" },
  { to: "/integrations" as const, icon: Puzzle, labelKey: "nav.integrations" },
  { to: "/dashboard" as const, icon: Settings, labelKey: "nav.settings" },
];

export function Sidebar({ userId, userName, userImage, recentThreads, onNewChat, onSignOut, onDeleteThread, onRenameThread }: SidebarProps) {
  const { t } = useTranslation("common");
  const matchRoute = useMatchRoute();
  const unreadCount = useQuery(api.emails.countUnread) ?? 0;
  // Thread lock (frontend-doctrine §4.16): during the first-run ritual there is
  // exactly ONE thread and no way to start a second. HIDDEN, not disabled —
  // and only on the confident `"onboarding"` mode, never while loading.
  const newChatHidden = useOnboardingStatus().mode === "onboarding";
  const { isDark, toggleTheme } = useTheme();
  const credit = useCreditBalance();
  const [supportOpen, setSupportOpen] = useState(false);
  const mode = useSidebarStore((s) => s.mode);
  const toggleMode = useSidebarStore((s) => s.toggleMode);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const [hoverActive, setHoverActive] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Effective expansion: pinned, hovered (after intent delay), focused inside,
  // or mid-rename/delete (don't collapse out from under the user's input).
  const isPinned = mode === "expanded";
  const isExpanded =
    isPinned ||
    hoverActive ||
    focusInside ||
    editingThreadId !== null ||
    confirmDeleteId !== null;
  const isRail = !isExpanded;

  function handleCopyUserId() {
    if (!userId) return;
    navigator.clipboard.writeText(userId);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  }

  function startRenaming(thread: RecentThread) {
    setEditingThreadId(thread._id);
    setEditingTitle(thread.title);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }

  function commitRename() {
    const trimmed = editingTitle.trim();
    if (editingThreadId && trimmed && onRenameThread) {
      onRenameThread({ threadId: editingThreadId, title: trimmed });
    }
    setEditingThreadId(null);
    setEditingTitle("");
  }

  function cancelRename() {
    setEditingThreadId(null);
    setEditingTitle("");
  }

  function handleDeleteThread(threadId: string) {
    if (onDeleteThread) {
      onDeleteThread({ threadId });
    }
    setConfirmDeleteId(null);
  }

  // Close sidebar on route change (mobile)
  const currentRoute = navItems.find((item) => matchRoute({ to: item.to, fuzzy: true }));
  useEffect(() => {
    setMobileOpen(false);
  }, [currentRoute?.to]);

  // Clear hover-intent timer on unmount.
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  function handleMouseEnter() {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverActive(true);
      hoverTimerRef.current = null;
    }, 150);
  }

  function handleMouseLeave() {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverActive(false);
  }

  function handleFocusCapture() {
    setFocusInside(true);
  }

  function handleBlurCapture(e: React.FocusEvent<HTMLElement>) {
    // `relatedTarget` is where focus is going. If it's still inside the aside, ignore.
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setFocusInside(false);
  }

  // Cmd/Ctrl+B pins/unpins sidebar (toggles persisted mode). Skip when target
  // is editable so we don't steal native Bold inside inputs.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "b") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      e.preventDefault();
      toggleMode();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleMode]);

  const visibleThreads = recentThreads?.slice(0, 5) ?? [];

  return (
    <>
      {/* Mobile hamburger removed — TopBar handles mobile nav */}

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        id="primary-nav"
        aria-label="Primary"
        aria-expanded={!isRail}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-52 flex-col border-r border-black/20 bg-surface-light font-bold transition-transform dark:border-white/10 dark:bg-surface-dark",
          "lg:translate-x-0 lg:transition-[width,box-shadow] lg:duration-150 motion-reduce:lg:transition-none",
          isRail ? "lg:w-14" : "lg:w-52",
          // Hover/focus overlay (not pinned) gets a soft shadow so it reads as floating over content.
          !isPinned && isExpanded && "lg:shadow-2xl",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Mobile close button */}
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute right-3 top-4 flex h-10 w-10 items-center justify-center rounded-lg text-black/50 hover:bg-black/5 dark:text-white/50 dark:hover:bg-white/5 lg:hidden"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      {/* Logo */}
      <div className={cn("flex h-16 items-center", isRail ? "lg:justify-center lg:px-2 px-6" : "px-6")}>
        <Link to="/" className="flex items-center gap-2 py-3" title="Clawcraft">
          <span className={cn(isRail && "lg:hidden")}>
            <BrandLogo markClassName="h-8" />
          </span>
          {isRail && <ClawMark className="hidden h-7 w-7 lg:block" />}
        </Link>
      </div>

      {/* Main Nav */}
      <nav className={cn("flex-1 space-y-1 overflow-y-auto py-4", isRail ? "lg:px-2 px-3" : "px-3")}>
        {!newChatHidden && (
          <button
            onClick={onNewChat}
            title={t("nav.newChat")}
            className={cn(
              "mb-4 flex w-full items-center rounded-full bg-home-yellow text-sm font-bold text-home-ink shadow-[0_8px_20px_-10px_rgba(33,31,28,0.4)] transition-all duration-200 hover:-translate-y-px hover:brightness-95",
              isRail ? "lg:justify-center lg:px-2 lg:py-2 gap-3 px-3 py-2.5" : "gap-3 px-3 py-2.5",
            )}
          >
            <MessageSquare className="h-5 w-5 shrink-0" />
            <span className={cn(isRail && "lg:hidden")}>{t("nav.newChat")}</span>
          </button>
        )}
        {navItems.map((item) => {
          const isActive = !!matchRoute({ to: item.to, fuzzy: true });
          const label = t(item.labelKey);
          return (
            <Link
              key={item.to}
              to={item.to}
              title={label}
              className={cn(
                "flex items-center rounded-lg text-sm font-medium transition-colors",
                isRail
                  ? "lg:justify-center lg:px-2 lg:py-2 gap-3 px-3 py-2.5"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-brand-coral-dark/10 text-brand-coral-dark"
                  : "text-black/60 hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white",
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className={cn(isRail && "lg:hidden")}>{label}</span>
              {item.to === "/inbox" && !isRail && <UnreadBadge count={unreadCount} />}
            </Link>
          );
        })}

        {/* Recent conversations — hidden in rail mode */}
        {!isRail && visibleThreads.length > 0 && (
          <>
            <div className="my-4 border-t border-black/10 dark:border-white/10" />
            <div className="px-3 pb-1 text-[10px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">
              History
            </div>
            {visibleThreads.map((thread) => (
              <div key={thread._id} className="group relative">
                {editingThreadId === thread._id ? (
                  <div className="flex items-center gap-1 rounded-lg px-3 py-1.5">
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-brand-coral" />
                    <input
                      ref={editInputRef}
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitRename();
                        if (e.key === "Escape") cancelRename();
                      }}
                      onBlur={commitRename}
                      className="min-w-0 flex-1 rounded border border-brand-coral/30 bg-transparent px-1.5 py-0.5 text-sm text-black outline-none focus:border-brand-coral dark:text-white"
                    />
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={commitRename}
                      className="shrink-0 rounded p-0.5 text-accent-green hover:bg-black/5 dark:hover:bg-white/5"
                      aria-label="Save"
                    >
                      <Check className="h-3 w-3" />
                    </button>
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={cancelRename}
                      className="shrink-0 rounded p-0.5 text-black/40 hover:bg-black/5 dark:text-white/40 dark:hover:bg-white/5"
                      aria-label="Cancel"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  confirmDeleteId === thread._id ? (
                    <div className="flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 dark:bg-red-950/30">
                      <span className="flex-1 text-xs text-red-600 dark:text-red-400">Delete?</span>
                      <button
                        onClick={() => handleDeleteThread(thread._id)}
                        className="rounded px-1.5 py-0.5 text-xs font-bold text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded px-1.5 py-0.5 text-xs font-bold text-black/40 hover:bg-black/5 dark:text-white/40 dark:hover:bg-white/5"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                  <Link
                    to="/chat/$threadId"
                    params={{ threadId: thread._id }}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                      matchRoute({ to: "/chat/$threadId", params: { threadId: thread._id } })
                        ? "bg-brand-coral/5 text-brand-coral"
                        : "text-black/50 hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:bg-white/5 dark:hover:text-white",
                    )}
                  >
                    <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{thread.title}</span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startRenaming(thread);
                      }}
                      className="ml-auto shrink-0 rounded p-0.5 text-black/0 transition-colors group-hover:text-black/30 group-hover:hover:text-black/60 dark:text-white/0 dark:group-hover:text-white/30 dark:group-hover:hover:text-white/60"
                      aria-label="Rename thread"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setConfirmDeleteId(thread._id);
                      }}
                      className="shrink-0 rounded p-0.5 text-black/0 transition-colors group-hover:text-black/30 group-hover:hover:text-red-500 dark:text-white/0 dark:group-hover:text-white/30 dark:group-hover:hover:text-red-400"
                      aria-label="Delete thread"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Link>
                  )
                )}
              </div>
            ))}
            <Link
              to="/chat-history"
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-colors",
                matchRoute({ to: "/chat-history", fuzzy: true })
                  ? "bg-brand-coral/5 text-brand-coral"
                  : "text-brand-coral hover:bg-brand-coral/5",
              )}
            >
              <History className="h-3.5 w-3.5 shrink-0" />
              <span>All Chats</span>
            </Link>
          </>
        )}

      </nav>

      {/* Bottom */}
      <div className={cn("space-y-3 py-4", isRail ? "lg:px-2 px-4" : "px-4")}>
        {/* User */}
        <div className={cn("flex items-center gap-3", isRail && "lg:justify-center")}>
          {userImage ? (
            <img src={userImage} alt="" className="h-8 w-8 rounded-full border border-black/10" title={isRail ? userName ?? "User" : undefined} />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-coral/20 text-xs font-bold text-brand-coral"
              title={isRail ? userName ?? "User" : undefined}
            >
              {userName?.[0]?.toUpperCase() ?? "?"}
            </div>
          )}
          {!isRail && (
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-black dark:text-white">
                {userName ?? "User"}
              </span>
              {userId && (
                <span className="flex items-center gap-1 text-[10px] text-black/40 dark:text-white/40">
                  <span className="truncate">{userId}</span>
                  <button
                    onClick={handleCopyUserId}
                    className="shrink-0 rounded p-0.5 transition hover:bg-black/5 hover:text-black/60 dark:hover:bg-white/5 dark:hover:text-white/60"
                    aria-label="Copy user ID"
                  >
                    <Copy className="h-2.5 w-2.5" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Credit pill — expanded only (rail has no room for text). */}
        {!isRail && (
          <CreditBalancePill balance={credit.balance} status={credit.status} />
        )}

        {/* Action row — theme / support / sign out.
            Rail mode: vertical stack of icon-only squares.
            Expanded mode: horizontal row. */}
        <div
          className={cn(
            "flex gap-1",
            isRail ? "lg:flex-col lg:items-center flex-row items-center" : "flex-row items-center",
          )}
        >
          <button
            onClick={toggleTheme}
            title={isDark ? "Light mode" : "Dark mode"}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-black/60 lg:h-8 lg:w-8 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setSupportOpen(true)}
            title="Support"
            aria-label="Open support"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-black/60 lg:h-8 lg:w-8 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            onClick={onSignOut}
            title="Sign out"
            aria-label="Sign out"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-black/60 lg:h-8 lg:w-8 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>

      </div>
    </aside>

      <SupportModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
      <Toast message="Copied!" isVisible={showCopied} />
    </>
  );
}
